/**
 * Loads CAPA source picklists: RCAs / deviations / audit findings / protocol risks
 * filtered to “eligible” items (no CAPA linked yet where applicable).
 */

import { useState, useEffect } from 'react';
import { rcaService } from '../services/rcaService';
import { deviationService } from '../services/deviationService';
import { auditServiceQMS } from '../services/auditService_qms';
import { riskService } from '../services/riskService';
import { protocolService } from '../services/protocolService';
import { userGroupService } from '../services/userGroupService';
import { capaService } from '../services/capaService';
import { auditLevelFindingId } from '../utils/capaAuditSource';

const AUDIT_CAPA_ACTION_TYPES = ['corectie', 'capa'];
const CLOSED_AUDIT_STATUSES = ['inchis'];

function protocolIdForDoc(p) {
  return p.firestoreId || p.id || p.protocolId || null;
}

/**
 * @param {string} organizationId
 * @param {{ initialRcaId?: string|null, initialDeviationId?: string|null, initialRiskId?: string|null }} initial
 */
export function useCapaReferenceOptions(organizationId, initial = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rcas, setRcas] = useState([]);
  const [deviations, setDeviations] = useState([]);
  const [auditFindings, setAuditFindings] = useState([]);
  const [risks, setRisks] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (!organizationId) {
      setRcas([]);
      setDeviations([]);
      setAuditFindings([]);
      setRisks([]);
      setGroups([]);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [rcasRaw, devsRaw, audits, protocols, allRisks, groupsRaw, capasRaw] =
          await Promise.all([
            rcaService.getRCAsByOrganization(organizationId),
            deviationService.getDeviationsByOrganization(organizationId, { status: 'all' }),
            auditServiceQMS.getByOrganization(organizationId),
            protocolService.getAllProtocols({}),
            riskService.getAllRisks(),
            userGroupService.getAllGroups({ isActive: true }),
            capaService.listByOrganization(organizationId)
          ]);

        if (cancelled) return;

        const rcasEligible = rcasRaw.filter(
          (r) => r.status !== 'closed' && (!r.capaIds || r.capaIds.length === 0)
        );

        const deviationsEligible = devsRaw.filter(
          (d) =>
            (d.status === 'open' || d.status == null || d.status === undefined) &&
            (!d.correctiveActionIds || d.correctiveActionIds.length === 0)
        );

        const auditIdsWithLevelCapa = new Set(
          (capasRaw || [])
            .filter((c) => c.sourceType === 'audit' && c.auditId)
            .map((c) => c.auditId)
        );

        const findings = [];
        for (const audit of audits) {
          const actions = audit.actions || [];
          let addedActionFinding = false;

          for (const a of actions) {
            if (AUDIT_CAPA_ACTION_TYPES.includes(a.type) && !a.capaId) {
              findings.push({
                auditId: audit.id,
                findingId: a.id,
                isAuditLevel: false,
                label: `${audit.title || audit.id} — ${(a.description || '').trim().slice(0, 80)}`
              });
              addedActionFinding = true;
            }
          }

          // Closed audit without corrective/CAPA actions — link CAPA to the audit itself
          if (
            CLOSED_AUDIT_STATUSES.includes(audit.status) &&
            !addedActionFinding &&
            !auditIdsWithLevelCapa.has(audit.id)
          ) {
            const closedLabel = audit.closedAt
              ? new Date(audit.closedAt).toLocaleDateString('ro-RO')
              : '';
            findings.push({
              auditId: audit.id,
              findingId: auditLevelFindingId(audit.id),
              isAuditLevel: true,
              label: closedLabel
                ? `${audit.title || audit.id} (închis ${closedLabel})`
                : `${audit.title || audit.id} (închis)`
            });
          }
        }

        findings.sort((a, b) => {
          if (a.isAuditLevel !== b.isAuditLevel) return a.isAuditLevel ? -1 : 1;
          return (a.label || '').localeCompare(b.label || '', 'ro');
        });

        const orgProtocols = protocols.filter(
          (p) => (p.organizationId || p.organization_id) === organizationId
        );
        const pidSet = new Set(orgProtocols.map(protocolIdForDoc).filter(Boolean));

        const risksEligible = allRisks
          .filter(
            (r) =>
              r.protocolId &&
              pidSet.has(r.protocolId) &&
              (r.status === 'Active' || r.status === 'active' || !r.status)
          )
          .sort((a, b) => (b.rpn || 0) - (a.rpn || 0));

        const groupsFiltered = groupsRaw.filter(
          (g) => !g.organizationId || g.organizationId === organizationId
        );

        let rcasMerged = [...rcasEligible];
        if (initial.initialRcaId && !rcasMerged.some((r) => r.id === initial.initialRcaId)) {
          const r = await rcaService.getRCA(initial.initialRcaId);
          if (r && r.organizationId === organizationId) {
            rcasMerged = [r, ...rcasMerged.filter((x) => x.id !== r.id)];
          }
        }

        let devsMerged = [...deviationsEligible];
        if (initial.initialDeviationId && !devsMerged.some((d) => d.id === initial.initialDeviationId)) {
          const d = await deviationService.getDeviation(initial.initialDeviationId);
          if (d && d.organizationId === organizationId) {
            devsMerged = [d, ...devsMerged.filter((x) => x.id !== d.id)];
          }
        }

        let risksMerged = [...risksEligible];
        if (initial.initialRiskId && !risksMerged.some((rk) => rk.id === initial.initialRiskId)) {
          const rk = await riskService.getRisk(initial.initialRiskId);
          if (rk) {
            const pid = rk.protocolId;
            if (pid && pidSet.has(pid)) {
              risksMerged = [rk, ...risksMerged.filter((x) => x.id !== rk.id)];
            }
          }
        }

        if (cancelled) return;

        setRcas(rcasMerged);
        setDeviations(devsMerged);
        setAuditFindings(findings);
        setRisks(risksMerged);
        setGroups(groupsFiltered);
      } catch (e) {
        console.error('[useCapaReferenceOptions]', e);
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    organizationId,
    initial.initialRcaId,
    initial.initialDeviationId,
    initial.initialRiskId
  ]);

  return { loading, error, rcas, deviations, auditFindings, risks, groups };
}
